import { useCallback, useMemo } from 'react';
import SearchableSelect from './SearchableSelect';
import { SELECT_MODE } from './constants';
import useInfiniteOptions from './useInfiniteOptions';
import useResolvedSelectValue from './useResolvedSelectValue';

const EMPTY_EXTRA_PARAMS = Object.freeze({});

/**
 * Infinite-scroll select with browse pagination and optional hybrid server search.
 */
export default function InfiniteSearchableSelect({
  fetchPage,
  extraParams = EMPTY_EXTRA_PARAMS,
  pageSize = 20,
  enabled = true,
  loadOnOpen = true,
  enableSearch = false,
  minRemoteSearchLength = 2,
  searchDebounceMs = 350,
  searchStrategy = 'hybrid',
  injectSelectedWhileSearching,
  valueId = null,
  selectedSeed = null,
  mapToOption = (option) => option,
  isSameOption = (a, b) => String(a?.id) === String(b?.id),
  getOptionId = (option) => option?.id,
  getOptionLabel: getOptionLabelProp,
  onChange,
  SelectComponent = SearchableSelect,
  error = false,
  helperText,
  ...props
}) {
  const getOptionLabel =
    getOptionLabelProp ||
    props.getOptionLabel ||
    ((option) => option?.label ?? option?.name ?? '');

  const { resolvedSelected, resolveOptionFromList, bindChange } = useResolvedSelectValue({
    valueId,
    selectedSeed,
    mapToOption,
    isSameOption,
  });

  const infinite = useInfiniteOptions({
    fetchPage,
    extraParams,
    pageSize,
    enabled,
    loadOnOpen,
    enableSearch,
    minRemoteSearchLength,
    searchDebounceMs,
    searchStrategy,
    injectSelectedWhileSearching,
    selectedValue: resolvedSelected,
    isOptionEqualToValue: isSameOption,
    getOptionId,
    getOptionLabel,
  });

  const selectedOption = useMemo(() => {
    if (enableSearch && infinite.searchMode !== 'browse') {
      return null;
    }
    return resolveOptionFromList(infinite.options);
  }, [enableSearch, infinite.options, infinite.searchMode, resolveOptionFromList]);

  const combinedHelperText = infinite.error
    ? `${infinite.error} Open the dropdown again to retry.`
    : helperText;

  const handleSearchInputChange = useCallback(
    (query) => {
      infinite.onSearchInputChange?.(query);

      if (
        enableSearch
        && query === ''
        && resolvedSelected
        && infinite.searchMode !== 'browse'
      ) {
        bindChange(onChange)(null);
      }
    },
    [
      bindChange,
      enableSearch,
      infinite.onSearchInputChange,
      infinite.searchMode,
      onChange,
      resolvedSelected,
    ]
  );

  return (
    <SelectComponent
      {...props}
      mode={SELECT_MODE.INFINITE}
      options={infinite.options}
      value={selectedOption}
      onChange={bindChange(onChange)}
      loading={infinite.loading}
      loadingMore={infinite.loadingMore}
      onOpen={infinite.onOpen}
      onClose={infinite.onClose}
      onLoadMore={infinite.onLoadMore}
      onSearchInputChange={enableSearch ? handleSearchInputChange : undefined}
      getOptionLabel={getOptionLabel}
      error={error || Boolean(infinite.error)}
      helperText={combinedHelperText}
      disabled={props.disabled || !enabled}
    />
  );
}
